import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowDown, ArrowUp, Plus, Save, Trash2, Truck } from "lucide-react";
import { toast } from "sonner";
import { NumberField, useFieldErrors } from "@/components/DecimalInput";
import { BRUTTO_OPTS, PERCENT_OPTS, INVALID_FORM_MSG, parseFields, toFieldText } from "@/lib/decimal";

const LABEL = { paket: "Paket", kleinpaket: "Kleinpaket", kein: "kein Label" };
const SPEC = { brutto: BRUTTO_OPTS, mwst_satz: PERCENT_OPTS };
const EMPTY = { name: "", brutto: "", mwst_satz: "19", label_typ: "paket", aktiv: true };

// Texte prüfen → Body für die API oder null (Fehler werden gesetzt, nie stilles 0).
const toBody = (f, setErrors) => {
  const r = parseFields(f, SPEC);
  if (!r.ok) { setErrors(r.errors); toast.error(INVALID_FORM_MSG); return null; }
  return { name: f.name, label_typ: f.label_typ, aktiv: f.aktiv, ...r.values };
};

const Row = ({ v, idx, count, canEdit, onSave, onDelete, onMove }) => {
  const [f, setF] = useState(() => toFieldText(v, SPEC));
  const [errors, setErrors, clearErr] = useFieldErrors();
  useEffect(() => setF(toFieldText(v, SPEC)), [v]);
  const set = (k) => (e) => { setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e })); clearErr(k); };
  const parsed = parseFields(f, SPEC);
  const dirty = f.name !== v.name || f.label_typ !== v.label_typ || f.aktiv !== v.aktiv || !parsed.ok
    || parsed.values.brutto !== Number(v.brutto) || parsed.values.mwst_satz !== Number(v.mwst_satz);
  const save = () => { const b = toBody(f, setErrors); if (b) onSave({ ...b, id: v.id }); };
  return (
    <tr className="border-t border-slate-100" data-testid={`versandart-row-${idx}`}>
      <td className="py-2 pr-2"><Input value={f.name} onChange={set("name")} disabled={!canEdit} className="h-8" data-testid={`versandart-name-${idx}`} /></td>
      <td className="py-2 pr-2 align-top w-28"><NumberField opts={BRUTTO_OPTS} value={f.brutto} onChange={set("brutto")} error={errors.brutto} onEnter={dirty ? save : undefined} disabled={!canEdit} className="h-8 w-24" aria-label="Bruttopreis in Euro" testId={`versandart-brutto-${idx}`} /></td>
      <td className="py-2 pr-2 align-top"><NumberField opts={PERCENT_OPTS} value={f.mwst_satz} onChange={set("mwst_satz")} error={errors.mwst_satz} disabled={!canEdit} className="h-8 w-16" aria-label="MwSt in Prozent" testId={`versandart-mwst-${idx}`} /></td>
      <td className="py-2 pr-2">
        <Select value={f.label_typ} onValueChange={set("label_typ")} disabled={!canEdit}>
          <SelectTrigger className="h-8 w-32" data-testid={`versandart-label-${idx}`}><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </td>
      <td className="py-2 pr-2"><Switch checked={f.aktiv} onCheckedChange={set("aktiv")} disabled={!canEdit} data-testid={`versandart-aktiv-${idx}`} /></td>
      {canEdit && (
        <td className="py-2 whitespace-nowrap text-right">
          <Button size="icon" variant="ghost" disabled={idx === 0} onClick={() => onMove(idx, -1)} data-testid={`versandart-up-${idx}`}><ArrowUp className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" disabled={idx === count - 1} onClick={() => onMove(idx, 1)} data-testid={`versandart-down-${idx}`}><ArrowDown className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" disabled={!dirty} onClick={save} data-testid={`versandart-save-${idx}`}><Save className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" onClick={() => onDelete(v)} data-testid={`versandart-delete-${idx}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
        </td>
      )}
    </tr>
  );
};

// Einstellungs-Karte „Versandarten (Positionen)“.
export const VersandartenSettings = ({ canEdit }) => {
  const [items, setItems] = useState([]);
  const [neu, setNeu] = useState(EMPTY);
  const [neuErrors, setNeuErrors, clearNeuErr] = useFieldErrors();
  const setN = (k) => (t) => { setNeu((x) => ({ ...x, [k]: t })); clearNeuErr(k); };
  const load = () => api.get("/versandarten").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const err = (e) => toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Speichern fehlgeschlagen.");
  const save = async ({ id, ...b }) => { try { await api.put(`/versandarten/${id}`, b); toast.success("Versandart gespeichert."); load(); } catch (e) { err(e); } };
  const create = async () => {
    const b = toBody(neu, setNeuErrors);
    if (!b) return;
    try { await api.post("/versandarten", b); setNeu(EMPTY); toast.success("Versandart angelegt."); load(); } catch (e) { err(e); }
  };
  const remove = async (v) => { if (!window.confirm(`„${v.name}“ löschen?`)) return; await api.delete(`/versandarten/${v.id}`); load(); };
  const move = async (idx, dir) => {
    const ids = items.map((x) => x.id);
    [ids[idx], ids[idx + dir]] = [ids[idx + dir], ids[idx]];
    const r = await api.put("/versandarten/reihenfolge", { ids }); setItems(r.data);
  };
  return (
    <Card className="border-slate-200" data-testid="versandarten-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><Truck className="w-4 h-4" /> Versandarten (Positionen)</CardTitle>
        <p className="text-sm text-slate-500 mt-1">Per Klick im Auftrag als Position einfügbar. Der Bruttopreis wird exakt so berechnet; der Label-Typ wird beim Versand vorausgewählt.</p>
      </CardHeader>
      <CardContent>
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-slate-500"><tr><th className="pb-1">Name</th><th className="pb-1 pr-2 text-right">Brutto (€)</th><th className="pb-1 pr-2 text-right">MwSt %</th><th className="pb-1">Label-Typ</th><th className="pb-1">Aktiv</th>{canEdit && <th />}</tr></thead>
          <tbody>
            {items.map((v, i) => <Row key={v.id} v={v} idx={i} count={items.length} canEdit={canEdit} onSave={save} onDelete={remove} onMove={move} />)}
            {canEdit && (
              <tr className="border-t border-slate-100 bg-slate-50" data-testid="versandart-new-row">
                <td className="py-2 pr-2"><Input placeholder="Neue Versandart…" value={neu.name} onChange={(e) => setNeu({ ...neu, name: e.target.value })} className="h-8" data-testid="versandart-new-name" /></td>
                <td className="py-2 pr-2 align-top"><NumberField opts={BRUTTO_OPTS} placeholder="0,00" value={neu.brutto} onChange={setN("brutto")} error={neuErrors.brutto} className="h-8 w-24" aria-label="Bruttopreis der neuen Versandart" testId="versandart-new-brutto" /></td>
                <td className="py-2 pr-2 align-top"><NumberField opts={PERCENT_OPTS} value={neu.mwst_satz} onChange={setN("mwst_satz")} error={neuErrors.mwst_satz} className="h-8 w-16" aria-label="MwSt der neuen Versandart" testId="versandart-new-mwst" /></td>
                <td className="py-2 pr-2">
                  <Select value={neu.label_typ} onValueChange={(v) => setNeu({ ...neu, label_typ: v })}>
                    <SelectTrigger className="h-8 w-32" data-testid="versandart-new-label"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="py-2 pr-2"><Switch checked={neu.aktiv} onCheckedChange={(v) => setNeu({ ...neu, aktiv: v })} /></td>
                <td className="py-2 text-right"><Button size="sm" onClick={create} disabled={!neu.name.trim()} data-testid="versandart-new-add"><Plus className="w-4 h-4 mr-1" /> Hinzufügen</Button></td>
              </tr>
            )}
          </tbody>
        </table>
        {items.length === 0 && !canEdit && <p className="text-xs text-slate-500 mt-2">Keine Versandarten angelegt.</p>}
      </CardContent>
    </Card>
  );
};
